import { formatDate } from '@localstack/services';
import { CICreditRangesType, ProductType, UsageSummary } from '@localstack/types';
import { PlanProductIcon, UsageLimit } from '@localstack/ui';
import { Typography, Skeleton, List, ListItem, ListItemAvatar, ListItemText, Box } from '@mui/material';
import { ReactElement } from 'react';

const CI_CREDIT_RANGES: CICreditRangesType = {
  green: [0, 70],
  orange: [70, 90],
  red: [90, 100],
  extra: [100, 200],
};

type UsageOverviewTypes = {
  isLoading: boolean;
  usageSummary: Optional<UsageSummary>;
  creditsUsed: number;
  quota: string | number;
};

export const UsageOverview = ({ isLoading, usageSummary, creditsUsed, quota }: UsageOverviewTypes): ReactElement => {
  const hasCIUsageLimit = typeof quota === 'number';

  const UsageLimitComponent = isLoading ? (
    <Skeleton variant="rectangular" height="150px" />
  ) : (
    <List disablePadding>
      <ListItem key="credits">
        <ListItemAvatar>
          <PlanProductIcon productType={ProductType.CI_USAGE} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              Usage ({formatDate(usageSummary?.usage_period_start ?? 0)} &ndash;{' '}
              {formatDate(usageSummary?.usage_period_end ?? 0)})
            </>
          }
          secondary={
            <Box pr={2}>
              {creditsUsed} Credits
              <UsageLimit
                variant="edged"
                greenRange={CI_CREDIT_RANGES.green}
                orangeRange={CI_CREDIT_RANGES.orange}
                redRange={CI_CREDIT_RANGES.red}
                value={hasCIUsageLimit ? Math.min(100, creditsUsed / quota) * 100 : 0}
                displayValue={`${creditsUsed} Credits`}
                tooltip={
                  <>
                    Consumed {creditsUsed} out of {quota || '∞'} CI credits in this billing period
                  </>
                }
              />
            </Box>
          }
        />
      </ListItem>
    </List>
  );
  return (
    <>
      <Typography variant="body1">
        To keep track of the CI use in different projects and pipelines it is recommended to create a separate CI Auth
        Token for each and give it a descriptive name.
      </Typography>

      {hasCIUsageLimit ? UsageLimitComponent : null}
    </>
  );
};
